/**
 * /es/comparisons/notion-ai — Draftlize vs Notion (Spanish). Links to
 * /es/registro-de-decisiones.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiEs() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['es']}
      documentTitle="Alternativa a Notion para producto: Draftlize vs Notion · Draftlize"
      documentDescription="Notion guarda páginas; Draftlize es un grafo de tarjetas que un agente de IA lee y escribe, para que las decisiones no se desvíen. Comparativa lado a lado."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Comparativa · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Draftlize vs Notion:<br />
            <em>¿cuál encaja en tu flujo de producto?</em>
          </h1>
          <p className="lede">
            Respuesta corta: Notion es la mejor herramienta para escribir cosas. Draftlize es un sustrato donde los agentes de IA pueden <em>leer y escribir</em> tu pensamiento de producto como tarjetas estructuradas. Resuelven problemas contiguos: más que sustituir uno por otro, se trata de decidir cuál necesitas de verdad ahora.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Resumen</span>
            <h2 className="serif">Cara a cara, <em>7 dimensiones.</em></h2>
          </div>
          <p>A partir de correr la misma hoja de ruta y los mismos escenarios en ambas herramientas.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Forma del sustrato</td><td className="ours yes">Grafo de tarjetas tipadas</td><td className="partial">Páginas de bloques</td></tr>
            <tr><td>La IA lee las decisiones como contexto cada vez</td><td className="ours yes">nativo</td><td className="no">—</td></tr>
            <tr><td>La IA escribe de vuelta como tarjetas estructuradas</td><td className="ours yes">nativo</td><td className="partial">escribe bloques, sin estructura</td></tr>
            <tr><td>Propagación «stale» al cambiar una dependencia</td><td className="ours yes">nativo</td><td className="no">—</td></tr>
            <tr><td>Documentos ricos y bases de datos</td><td className="no">—</td><td className="yes">excelente</td></tr>
            <tr><td>Wiki de equipo · permisos · colaboración</td><td className="no">individual (v1)</td><td className="yes">excelente</td></tr>
            <tr><td>Integración Claude Code / MCP</td><td className="ours yes">nativo</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Dónde <em>gana Notion.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Si necesitas una wiki</h3>
            <p>Notion es la mejor wiki que existe. Si tu problema es «dónde guarda el equipo el manual de incidencias», Notion. Draftlize no intenta ser una wiki.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Si vives en tablas y bases de datos</h3>
            <p>El sistema de bases de datos y vistas de Notion es imbatible. Tracker de roadmap, tableros de sprint, tablas de evaluación: para esas formas, las colecciones estructuradas de Notion siempre ganan.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Si tu equipo es grande</h3>
            <p>Colaboración en tiempo real, permisos finos, comentarios, menciones: Notion se hizo para eso. Draftlize es individual por diseño.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Dónde <em>gana Draftlize.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">IA que <em>de verdad</em> recuerda</h3>
            <p>La «memoria» de Notion AI es una recuperación best-effort hacia el chat. En Draftlize, el sustrato en el que trabaja el agente son las tarjetas, así que lee tu decisión de precios en cada chat, automáticamente. La reexplicación baja a cero.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">La desviación se vuelve visible</h3>
            <p>Cambias una página de Notion → nadie se entera. Cambias una tarjeta de decisión en Draftlize → cada tarjeta dependiente se marca <code>stale</code>. El sustrato rastrea la invalidación como un sistema de build.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Contexto de primera para Claude Code / Cursor / MCP</h3>
            <p>Notion AI vive en Notion. Las tarjetas de Draftlize aterrizan en tu repo vía CLI y se exponen por MCP a cada herramienta de IA. Las decisiones son referenciables desde cualquiera. Sigue con <a href="/es/registro-de-decisiones" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro de decisiones</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion responde a «dónde apunta las cosas el equipo». Draftlize responde a «dónde se acumula el pensamiento de producto cuando la IA hace la mitad del trabajo».
        </blockquote>
        <cite>Si solo necesitas la primera respuesta, Notion basta.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Empieza gratis con <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Puedes quedarte con Notion. Draftlize es el sustrato debajo de las partes asistidas por IA. Regístrate y recibe $5, paga solo por lo que usas, el saldo nunca caduca.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
